import { useCallback, useEffect, useState } from "react";
import { jsonFromDocument } from "cerulean";
import { InstitutionSetting } from "../types";

const settingUrl = (settingName: string) => `/api/institution_settings/${settingName}/`;

// Our error responses are a mess. Don't overthink this
const messageFromErrorBody = (data: unknown): string => {
  if (!data || typeof data !== "object") return "";
  const { detail, message } = data as { detail?: unknown; message?: unknown };

  if (typeof detail === "string") return detail;
  if (typeof message === "string") return message;

  if (message && typeof message === "object") {
    const fieldErrors = Object.entries(message)
      .filter(([field]) => field !== "id")
      .map(([field, errors]) => `${field}: ${[].concat(errors as never).join(" ")}`);
    if (fieldErrors.length) return fieldErrors.join("; ");
  }

  return "";
};

const errorFromResponse = async (response: Response, fallback: string) => {
  try {
    return messageFromErrorBody(await response.json()) || fallback;
  } catch {
    return fallback;
  }
};

const useInstitutionSetting = (settingName: string) => {
  const [setting, setSetting] = useState<InstitutionSetting | null>(null);

  useEffect(() => {
    let isStale = false;

    const fetchSetting = async () => {
      const response = await fetch(settingUrl(settingName));
      const data = await response.json();
      if (!isStale) {
        setSetting(data);
      }
    };

    fetchSetting();
    return () => {
      isStale = true;
    };
  }, [settingName]);

  const save = useCallback(
    async (value: string, note: string) => {
      const response = await fetch(settingUrl(settingName), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": jsonFromDocument("csrf_token"),
        },
        body: JSON.stringify({ value, note }),
      });

      if (!response.ok) {
        throw new Error(await errorFromResponse(response, "Failed to save setting."));
      }

      const data = await response.json();
      setSetting(data);
      return data as InstitutionSetting;
    },
    [settingName]
  );

  return { setting, save };
};

export default useInstitutionSetting;
